import { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";

const sections = [
  { id: "information", label: "Information We Collect" },
  { id: "consent", label: "Consent" },
  { id: "disclosure", label: "Disclosure" },
  { id: "payment", label: "Payment" },
  { id: "third-party", label: "Third Party Services" },
  { id: "security", label: "Security" },
  { id: "cookies", label: "Cookies" },
  { id: "age", label: "Age of Consent" },
  { id: "changes", label: "Changes to This Privacy Policy" },
  { id: "contact", label: "Contact Information" },
];

const PrivacyPolicy = () => {
  const [activeId, setActiveId] = useState("information");

  useEffect(() => {
    const observers = [];
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: "-10% 0px -85% 0px", threshold: 0 },
      );
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const scrollTo = (id) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <div className="w-full bg-gray-100">
        <Navbar />

        {/* Banner */}
        <div className="mt-5 mx-4 md:mx-20">
          <div className="flex items-center rounded-2xl bg-[#01C37D] min-h-[200px] md:min-h-[300px] overflow-hidden">
            <div className="w-full grid grid-cols-12">
              <div className="col-span-2 hidden md:block"></div>
              <div className="col-span-12 md:col-span-10 px-6 md:px-0">
                <h1 className="text-white text-2xl md:text-3xl lg:text-5xl font-bold">
                  Privacy Policy
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="px-4 md:px-20 py-6 md:py-10 mt-5 md:mt-6">
          <div className="flex gap-6 items-start">
            {/* LEFT - Sticky TOC (hidden on mobile) */}
            <div className="hidden lg:block w-1/3 sticky top-28">
              <div className="bg-white rounded-2xl px-8 py-6 max-h-[80vh] overflow-y-auto">
                <p className="text-2xl font-semibold mb-4">Table of Content</p>
                <ul className="space-y-2">
                  {sections.map(({ id, label }) => (
                    <li
                      key={id}
                      onClick={() => scrollTo(id)}
                      className={`cursor-pointer px-3 py-1.5 rounded-lg text-sm transition-all duration-200 ${activeId === id
                          ? "bg-green-100 text-green-700 font-semibold border-l-4 border-[#01C37D] pl-2"
                          : "text-gray-600 hover:text-green-600 hover:bg-gray-50"
                        }`}
                    >
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* RIGHT - Content (full width on mobile) */}
            <div className="w-full lg:w-2/3">
              <div className="bg-white rounded-2xl pb-10 md:pb-20">

                <div className="px-6 md:px-12 space-y-6 p-5">
                  <div id="information" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Information We Collect
                    </p>
                    <p>
                      This Privacy Policy explains how Anlook (managed by Chaafo Private Limited and marketed by Bumppy Media Pvt. Ltd.) collects, uses, and protects your personal information when you visit anlook.com.
                      . This Privacy Policy, effective from August
                      2024, covers how the information is collected, used, and
                      disclosed whenever you visit Anlook.com, which is now
                      operated by Chaafo Private Limited, or when you make any
                      purchase.
                    </p>


                    <p>
                      If you please grant us the request, we can, at some
                      intervals, send you emails notifying you about the store,
                      its new stock, or any relevant changes. However, we may
                      need to gather some information, not limited to, your name
                      and email for the purposes of buying or selling.
                      Furthermore, the system automatically collects your IP
                      address whenever you access our site. That allows us to
                      know the system and the browser that is being used.
                    </p>
                  </div>

                  <div id="consent" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Consent
                    </p>
                    <p className="text-green-800">
                      <strong>How is consent obtained?</strong>
                    </p>
                    <p className="mt-2">
                      When you give us personal details to perform any tasks
                      such as completing a transaction, validating your credit
                      card, making an order, managing a delivery, or returning
                      an item, you are consenting to collection and use for that
                      particular purpose. Simply put, your cell number serves as
                      your unique identifier, which, as part of license
                      management, is kept in our system. We may use that number
                      to occasionally contact you with helpful information on
                      using our products. If you wish to have your phone number
                      unsaved, you can stop using the app and notify{" "}
                      <a
                        href="https://mail.google.com/mail/?view=cm&fs=1&to=support@Chaafo.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        support@Chaafo.com
                      </a>{" "}
                      that you would like to have it removed. We will delete
                      your number from our data within 72 working hours and
                      acknowledge the notice.
                    </p>
                    <p className="mt-2">
                      We will either directly ask for your affirmative consent
                      or give you the option to decline if we ever ask for your
                      information for a purpose other than the one listed, like
                      marketing products to you.
                    </p>
                    <p className="mt-2 text-green-800">
                      <strong>How do I withdraw my consent?</strong>
                    </p>
                    <p className="mt-2">
                      You can email us at{" "}
                      <a
                        href="https://mail.google.com/mail/?view=cm&fs=1&to=support@Chaafo.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        support@Chaafo.com
                      </a>{" "}
                      to revoke your consent for contact or for the ongoing
                      collection, use, and/or disclosure of information if you
                      change your mind after opting in.
                    </p>
                  </div>

                  <div id="disclosure" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Disclosure
                    </p>
                    <p>
                      We may disclose your personal information if required to
                      do so by law, or in the event you breach our Terms of
                      Service.
                    </p>
                  </div>

                  <div id="payment" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Payment
                    </p>
                    <p>
                      Payments shall be processed using the Razorpay Payment
                      Gateway. To be clear, Razorpay does not keep your card
                      information on its servers. In accordance with the Payment
                      Card Industry Data Security Standard (PCIDSS), the data
                      related to your payment transaction is encrypted
                      throughout the process. Purchase transaction data is
                      stored only so long as required to complete the purchase.
                      Thereafter, no further retention of information pertaining
                      to the purchase transaction will be done.
                    </p>
                    <p className="mt-2">
                      The payment gateway follows the PCIDSS as set forth by the
                      PCI Security Standards Council, which is comprised of
                      joint efforts from Visa, MasterCard, American Express,
                      Discover, and others. These standards guarantee secure
                      handling of credit card information by our store and
                      service providers. For further information please visit
                      Razorpay's terms and conditions https://razorpay.com.
                    </p>
                  </div>

                  <div id="third-party" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Third-party sellers
                    </p>
                    <p>
                      Generally care about collecting, using, and disclosing
                      only that information necessary to offer services to us.
                      However, certain third-party services such as payment
                      gateways, and other payment processors have their own
                      privacy policies regarding the information we provide them
                      for your purchase-related transactions. We advise you to
                      read their privacy policies to see how your personal
                      information will be treated by these providers.
                    </p>
                    <p className="mt-2">
                      Bear in mind that some providers may be located in, or
                      have facilities in, a different jurisdiction from yours,
                      or ours. If you initiate any transaction involving the
                      services of a third-party service provider, your
                      information may be subjected to the applicable laws of the
                      jurisdiction(s) where that service provider or its
                      facilities are located. Once any of your information
                      leaves our website, or is redirected from our website onto
                      the third-party website or application, this Privacy
                      Policy and the Terms of Service for our website cease to
                      be applicable.
                    </p>
                  </div>

                  <div id="security" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Security
                    </p>
                    <p>
                      Taking reasonable precautions and following the best
                      practices of the industry, we are very well able to
                      confirm that your personal information does not get lost,
                      misused, accessed, disclosed, altered or destroyed in any
                      inappropriate way.
                    </p>
                  </div>

                  <div id="cookies" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Cookies
                    </p>
                    <p>
                      Cookies are used to keep your user session going. It does
                      not identify you on any other website.
                    </p>
                  </div>

                  <div id="age" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Age of Consent
                    </p>
                    <p>
                      You are of legal age in your country or state of residence
                      by using this site, or you are of the age of majority in
                      your state or province of residence and you have given us
                      permission to enable any of your dependent minors to use
                      this site.
                    </p>
                  </div>

                  <div id="changes" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Amendments Made to this Privacy Policy
                    </p>
                    <p>
                      We may change this Privacy Policy at any time, so do well
                      to review it often. Such changes and clarifications will
                      take effect immediately upon their posting on our website.
                      If we make changes to this policy in such a way, we shall
                      leave a notice here, informing you the things, which we
                      collect, how we use it, and under what circumstance if
                      any, we would use and/or disclose it. Your information
                      could then be passed onto the new owners if they buy the
                      store or merge with another company so that we can
                      continue selling products to you.
                    </p>
                  </div>

                  <div id="contact" className="scroll-mt-24">
                    <p className="text-xl font-semibold mb-2 text-green-800">
                      Contact Information
                    </p>
                    <p>
                      You may reach out to our Privacy Compliance Officer if you
                      want to access, correct, amend, or delete any personal
                      information we have about you, if you would like to
                      register a complaint, or if you would just like
                      information at{" "}
                      <a
                        href="https://mail.google.com/mail/?view=cm&fs=1&to=support@Chaafo.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        support@Chaafo.com
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
